// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Base class for editor interfaces which interact with and update
 * rendered deck canvas.
 * @author James
 */

// @ts-check

// @@imports-dependencies
import { Deck, Edge, Vector2D } from "../../../../../package/index.js"

// @@imports-package
import { DeckRenderer, LoadingContainer } from "../../index.js"

// @@imports-package
import { tweenDeckEditor } from "../../../layouts/deck-editor/tween.js"
import { App } from "../../../app/index.js"

// @@body
class Editor extends HTMLElement {
    tweening = false

    constructor() {
        super()

        this.renderer.selectedEdge ??= this.deck.edges.array[0]
    }

    connectedCallback() {
        const observer = new ResizeObserver(() => {
            const { height: editorHeight } = this.parentElement.getBoundingClientRect()
            const { height: toolbarHeight } = /** @type {HTMLElement} */
                (document
                    .querySelector("nav.deck-toolbar"))
                    .getBoundingClientRect()

            const offset = new Vector2D(0, - editorHeight / 2)
                .subtract(this.renderer.offsets.fixed)
            const correction = - editorHeight - this.renderer.canvasHeightCorrections.fixed
            this.#tweenRenderer(offset, correction)
        })

        observer.observe(this.parentElement)
        this.observer = observer
    }

    disconnectedCallback() {
        this.observer?.disconnect()
    }

    async remove() {
        this.observer?.disconnect()
        this.#tweenRenderer()
        await App.container.unloadPopover()
        super.remove()
    }

    #tweenRenderer(targetOffset = new Vector2D(0, 0), targetCorrection = 0) {
        const originalOffset = this.renderer.offsets.app
        const originalCorrection = this.renderer.canvasHeightCorrections.app

        if (
            targetOffset.equals(originalOffset) &&
            targetCorrection === originalCorrection
        ) { return }

        this.renderer.tween(progress => {
            this.renderer.offsets.app = targetOffset
                .subtract(originalOffset)
                .scalarMultiply(progress)
                .add(originalOffset)
            this.renderer.canvasHeightCorrections.app = originalCorrection +
                progress * (targetCorrection - originalCorrection)
        })
    }

    get container() {
        return App.instance.container
    }

    get renderer() {
        return /** @type {DeckRenderer} */ (App.instance.state.deck.renderer)
    }

    get deck() {
        return /** @type {Deck} */ (App.instance.state.deck.client)
    }

    static get customName() {
        return "editor-popover"
    }
}

// @@exports
export { Editor }
